import clsx from 'clsx'
import { upperFirst } from 'lodash-es'

import type { QuoteReasonPayload } from '@fv/models'

import type { PendingLoadToBook } from '@/features/booking/types'
import { CompactQuoteReasonForm } from '@/features/quote-reason/CompactQuoteReasonControls'
import { useNeedsQuoteReason } from '@/features/quote-reason/hooks'
import { usePrettyNumber, useTypeNames } from '@/hooks/settings'
import { useQuotes } from '@/hooks/shipments'

import { Pinline } from './Pinline'
import { Price } from './shared/Price'

export type AwardCarrierLoad = Pick<
  PendingLoadToBook,
  'equipment' | 'loadId' | 'status' | 'quoteId'
>

type AwardCarrierRowProps = {
  load: AwardCarrierLoad
  quoteId?: string
  truckNumber: number
  quoteReason: QuoteReasonPayload | undefined
  onQuoteReasonChange?: (reason: QuoteReasonPayload) => void
}

const AwardCarrierRow = ({
  load,
  quoteId,
  truckNumber,
  quoteReason,
  onQuoteReasonChange,
}: AwardCarrierRowProps) => {
  const { equipment, loadId, status } = load
  const { equipmentName } = useTypeNames()
  const isAwarded = status === 'awarded' || status === 'confirmed'
  const prettyNumber = usePrettyNumber()
  const quotesQuery = useQuotes(loadId)
  const quote = quotesQuery.data?.find(q => q._id === quoteId)
  const needsReason = useNeedsQuoteReason()(quotesQuery.data ?? [], quote)

  return (
    <>
      <div className="flex items-start">
        <div
          className={clsx(
            'w-8 h-8 rounded-full flex items-center justify-center border-2 border-fv-gray-dark',
            {
              'bg-fv-gray-dark text-white': !isAwarded && Boolean(quote),
            },
          )}
        >
          {truckNumber}
        </div>
        <div className="ml-4 flex-auto">
          <div className="mb-0" style={{ lineHeight: '1.4em' }}>
            Truck {truckNumber} /{' '}
            {equipmentName(quote?.equipmentType ?? equipment.type)} /{' '}
            {upperFirst(equipment.description)}
            <br />
            {prettyNumber(equipment.weight)} {equipment.weightUOM}.
            {equipment.declaredValue &&
              ` / $${prettyNumber(equipment.declaredValue)}`}
            <div className="flex items-center">
              <div>
                {quote?.providerName || 'No carriers'} selected to take this
                load.
              </div>
              <Pinline flex className="mx-1" />
              {quote ? (
                <>
                  {isAwarded && <span>Awarded&nbsp;</span>}
                  <Price amount={quote?.amount} currency={quote?.currency} />
                </>
              ) : (
                <div>Not selected</div>
              )}
            </div>
          </div>
          {needsReason && onQuoteReasonChange && (
            <div className="pt-4">
              <CompactQuoteReasonForm
                value={quoteReason ?? { reasonType: 'urgent' }}
                onChange={onQuoteReasonChange}
              />
            </div>
          )}
        </div>
      </div>
      <Pinline rule />
    </>
  )
}

export default AwardCarrierRow
